import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import {
  ChevronDown,
  ChevronRight,
  ChevronLeft,
  ArrowRight,
  ArrowLeft,
  Check,
  Zap,
  Star,
  Quote,
} from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { guideTypeIds, quoteOnlyTypeIds, getServiceTypeImage, getProductsForTranslationType, translationTypeFilters } from '../data/navServices';
import { getMainService, isMainService, serviceName, serviceDesc } from '../data/mainServices';
import { getInterpretingService, interpretingServices } from '../data/interpretingServices';
import { serviceProducts, getProductImage, getProductPriceRange } from '../data/serviceProducts';
import { useSubServices } from '../data/subServicesStore';
import { formatEur, tndToEur } from '../utils/currency';
import assetUrl from '../assetUrl';
import './ServiceTypePage.css';
import './AllServicesPage.css';
import '../components/home/ReviewsSection.css';

function FaqItem({ item, open, onToggle }) {
  return (
    <div className={`stp-faq-item${open ? ' is-open' : ''}`}>
      <button type="button" className="stp-faq-q" onClick={onToggle} aria-expanded={open}>
        <span>{item.q}</span>
        <ChevronDown size={18} aria-hidden="true" />
      </button>
      {open && (
        <div className="stp-faq-a">
          {item.a && <p>{item.a}</p>}
          {item.list?.length > 0 && (
            <ul>
              {item.list.map((line) => (
                <li key={line}>{line}</li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

function ReviewsCarousel({ items }) {
  const windowRef = useRef(null);
  const [isHovered, setIsHovered] = useState(false);

  const scroll = (dir) => {
    const el = windowRef.current;
    if (!el) return;
    const card = el.querySelector('.stp-carousel-slide');
    const step = card ? card.offsetWidth + 24 : 300;
    if (dir === 'next') {
      if (el.scrollLeft + el.offsetWidth >= el.scrollWidth - 2) {
        el.scrollTo({ left: 0, behavior: 'smooth' });
      } else {
        el.scrollBy({ left: step, behavior: 'smooth' });
      }
    } else {
      if (el.scrollLeft <= 0) {
        el.scrollTo({ left: el.scrollWidth, behavior: 'smooth' });
      } else {
        el.scrollBy({ left: -step, behavior: 'smooth' });
      }
    }
  };

  useEffect(() => {
    if (!items || items.length === 0 || !windowRef.current) return undefined;
    const el = windowRef.current;
    const timer = setInterval(() => {
      if (!el || isHovered) return;
      scroll('next');
    }, 4000);
    return () => clearInterval(timer);
  }, [items, isHovered]);

  if (!items || items.length === 0) return null;

  return (
    <div
      className="stp-carousel"
      style={{ padding: '0 40px' }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <button type="button" className="stp-carousel-arrow stp-carousel-arrow--prev" onClick={() => scroll('prev')} aria-label="Précédent">
        <ChevronLeft size={28} />
      </button>
      <div ref={windowRef} className="stp-carousel-window">
        <div className="stp-carousel-track">
          {items.map((r) => (
            <div key={r.name} className="stp-carousel-slide review-card" style={{ height: '100%' }}>
              <div className="review-card-inner" style={{ gap: '16px' }}>
                <Quote size={28} className="review-quote-icon" aria-hidden="true" />
                <div className="review-stars" aria-label="5 sur 5">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} size={16} className="star-filled" fill="currentColor" aria-hidden="true" />
                  ))}
                </div>
                <p className="review-text">&ldquo;{r.text}&rdquo;</p>
                <div className="review-footer">
                  <div className="review-avatar" style={{ background: 'var(--teal)' }}>{r.initial}</div>
                  <div className="review-author">
                    <strong>{r.name}</strong>
                    <span>{r.role}</span>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  <span className="review-doc">{r.type}</span>
                  <span className="review-doc">{r.from} → {r.to}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      <button type="button" className="stp-carousel-arrow stp-carousel-arrow--next" onClick={() => scroll('next')} aria-label="Suivant">
        <ChevronRight size={28} />
      </button>
    </div>
  );
}

function DocsCarousel({ title, items, seeAllLabel }) {
  const { t } = useLanguage();
  const windowRef = useRef(null);

  useEffect(() => {
    if (!items || items.length === 0 || !windowRef.current) return undefined;
    const el = windowRef.current;
    const timer = setInterval(() => {
      if (!el) return;
      const card = el.querySelector('.stp-carousel-slide');
      const step = card ? card.offsetWidth + 16 : 300;
      if (el.scrollLeft + el.offsetWidth >= el.scrollWidth - 2) {
        el.scrollTo({ left: 0, behavior: 'smooth' });
      } else {
        el.scrollBy({ left: step, behavior: 'smooth' });
      }
    }, 4000);
    return () => clearInterval(timer);
  }, [items]);

  if (!items || items.length === 0) return null;

  const scroll = (dir) => {
    const el = windowRef.current;
    if (!el) return;
    const card = el.querySelector('.stp-carousel-slide');
    const step = card ? card.offsetWidth + 16 : 300;
    if (dir === 'next') {
      if (el.scrollLeft + el.offsetWidth >= el.scrollWidth - 2) {
        el.scrollTo({ left: 0, behavior: 'smooth' });
      } else {
        el.scrollBy({ left: step, behavior: 'smooth' });
      }
    } else {
      if (el.scrollLeft <= 0) {
        el.scrollTo({ left: el.scrollWidth, behavior: 'smooth' });
      } else {
        el.scrollBy({ left: -step, behavior: 'smooth' });
      }
    }
  };

  return (
    <section className="stp-carousel-section">
      <div className="stp-carousel-inner">
        <h2 className="stp-heading">{title}</h2>
        <div className="stp-carousel">
          <button type="button" className="stp-carousel-arrow stp-carousel-arrow--prev" onClick={() => scroll('prev')} aria-label="Précédent">
            <ChevronLeft size={28} />
          </button>
          <div ref={windowRef} className="stp-carousel-window">
            <div className="stp-carousel-track">
              {items.map((item) => (
                <div key={item.slug || item.title} className="stp-carousel-slide">
                  <div className="all-services-card" style={{ height: '100%' }}>
                    <div className="all-services-card-media">
                      <img
                        src={item.image}
                        alt={item.title}
                        loading="lazy"
                        decoding="async"
                        onError={(e) => {
                          e.target.src = assetUrl('/images/services/docs/doc-birth.jpg');
                        }}
                      />
                      {item.hasPrice !== false && (
                        <Link to="/order" className="asc-order" aria-label="Commander en ligne">
                          <span>Commander</span>
                        </Link>
                      )}
                    </div>
                    <div className="all-services-card-body">
                      <h3>{item.title}</h3>
                      {item.hasPrice !== false && (
                        <div className="all-services-card-rating">
                          <div className="all-services-card-stars" aria-label={`${item.reviewLabel} ${t.serviceCatalog.reviews}`}>
                            {[...Array(5)].map((_, i) => (
                              <Star key={i} size={14} fill={item.rating > 0 ? 'currentColor' : 'none'} aria-hidden="true" />
                            ))}
                          </div>
                          <span className="all-services-card-reviews">{item.reviewLabel} {t.serviceCatalog.reviews}</span>
                        </div>
                      )}
                      {!item.quote && (
                        <p className="all-services-card-price">
                          <span className="all-services-card-from">{t.serviceCatalog.from}</span>
                          <strong>{item.rangeMin} TND</strong>
                          <span className="all-services-card-price-separator">/</span>
                          <span className="all-services-card-tnd">{item.priceEur}</span>
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <button type="button" className="stp-carousel-arrow stp-carousel-arrow--next" onClick={() => scroll('next')} aria-label="Suivant">
            <ChevronRight size={28} />
          </button>
        </div>
        {seeAllLabel && (
          <div className="stp-carousel-see-all">
            <Link to="/tous-les-services">{seeAllLabel}</Link>
          </div>
        )}
      </div>
    </section>
  );
}

export default function ServiceTypePage() {
  const { typeId } = useParams();
  const { t, lang, isRTL } = useLanguage();
  const locale = lang === 'ar' ? 'ar-TN' : lang === 'en' ? 'en-GB' : 'fr-FR';
  const st = t.serviceTypes || {};
  const p = st.page || {};
  const menu = useMemo(() => t.nav?.servicesMenu || {}, [t.nav?.servicesMenu]);
  // Service JSON sans contenu i18n → page générique (titre + description du JSON)
  const jsonService = getMainService(typeId);
  const jsonServiceName = serviceName(jsonService, lang);
  // Interprétariat : type enregistré dans config/interpreting.json
  const interpService = getInterpretingService(typeId);
  const jsonDesc = serviceDesc(jsonService || interpService, lang);
  const type = st.types?.[typeId]
    || (jsonService || interpService
      ? { title: jsonServiceName || serviceName(interpService, lang), pageTitle: jsonServiceName || serviceName(interpService, lang), cardDesc: jsonDesc, sections: [] }
      : null);
  // Le nom du services.json / interpreting.json (fr/en/ar) est prioritaire sur le titre i18n
  const jsonTitle = serviceName(jsonService, lang) || serviceName(interpService, lang);
  const displayType = type && jsonTitle
    ? { ...type, title: jsonTitle, pageTitle: jsonTitle, cardDesc: jsonDesc || type.cardDesc }
    : type;
  const [openFaq, setOpenFaq] = useState(0);

  const catalog = t.serviceCatalog || {};
  const typeProducts = useMemo(
    () => getProductsForTranslationType(typeId, serviceProducts),
    [typeId],
  );

  // Sous-services dynamiques (DB) rattachés à ce service principal
  const subServices = useSubServices(typeId);
  // Types d'interprétariat : DB (prix/avis) si serveur en ligne — sinon interpreting.json
  const interpSubs = useSubServices('interpreting');

  const totalReviews = useMemo(
    () => typeProducts.reduce((sum, p) => sum + (p.rating || 0), 0),
    [typeProducts],
  );

  const carouselItems = useMemo(() => {
    const products = typeProducts.length > 0 ? typeProducts : serviceProducts.filter((p) => p.featured);
    return products.map((product) => {
      const copy = catalog.products?.[product.id];
      const range = getProductPriceRange(product);
      return {
        title: copy?.title || product.id,
        image: assetUrl(getProductImage(product)),
        slug: product.slug,
        rangeMin: range.min,
        priceEur: formatEur(tndToEur(range.min), locale),
        unit: product.unit,
        rating: product.rating || 0,
        reviewLabel: (product.rating || 0).toLocaleString(locale),
      };
    });
  }, [typeProducts, catalog.products, locale]);

  const interpretingCarouselItems = useMemo(() => {
    // Serveur en ligne → items DB (prix + avis) limités aux slugs du interpreting.json
    const fromDb = (interpSubs.items || [])
      .filter((s) => getInterpretingService(s.slug))
      .map((s) => {
        const cfg = getInterpretingService(s.slug);
        const hasPrice = (s.basePrice || 0) > 0;
        return {
          slug: cfg.slug,
          title: (lang === 'ar' ? s.titleAr || cfg.nameAr : lang === 'en' ? s.titleEn || cfg.nameEn : s.titleFr || cfg.name) || cfg.name,
          image: assetUrl(s.image || cfg.image || getServiceTypeImage(cfg.slug)),
          rating: s.rating || 0,
          reviewLabel: (s.rating || 0).toLocaleString(locale),
          rangeMin: s.basePrice,
          priceEur: formatEur(tndToEur(s.basePrice || 0), locale),
          unit: s.unit,
          quote: !hasPrice,
          hasPrice,
        };
      });
    if (fromDb.length) return fromDb;
    // Hors-ligne → config interpreting.json, sans prix ni avis
    return interpretingServices.map((s) => ({
      slug: s.slug,
      title: serviceName(s, lang),
      image: assetUrl(s.image || getServiceTypeImage(s.slug)),
      rating: 0,
      reviewLabel: '0',
      quote: true,
      hasPrice: false,
    }));
  }, [interpSubs.items, lang, locale]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setOpenFaq(0);
  }, [typeId]);

  if (!typeId || (!guideTypeIds.includes(typeId) && !isMainService(typeId) && !interpService) || !type) {
    return <Navigate to="/services" replace />;
  }

  const sections = displayType.sections || [];
  const title = displayType.pageTitle || displayType.title || menu[typeId];
  const lead = displayType.pageLead || displayType.cardDesc || menu[`${typeId}Desc`];
  const Arrow = isRTL ? ArrowLeft : ArrowRight;
  const image = getServiceTypeImage(typeId);
  const supportsOnlineOrder = !quoteOnlyTypeIds.includes(typeId);
  const interpretingTypeIds = translationTypeFilters
    .filter((f) => f.productIds?.includes('onlineInterpreter'))
    .map((f) => f.id);
  const isInterpretingType = interpretingTypeIds.includes(typeId);

  const carouselTitle = isInterpretingType
    ? "Tous les services d'interprétariat"
    : (sections.find((s) => s.list?.length > 0)?.title || p.docsTitle);

  return (
    <div className={`stp${isRTL ? ' rtl' : ''}`}>
      {/* ── Bannière ── */}
      <header className="stp-banner">
        <div
          className="stp-banner-bg"
          style={{ backgroundImage: `url(${assetUrl(image)})` }}
          aria-hidden="true"
        />
        <div className="stp-banner-veil" aria-hidden="true" />

        <div className="container stp-banner-inner">
          <h1>{title}</h1>
          <nav className="stp-breadcrumb" aria-label="Breadcrumb">
            <Link to="/">{p.breadcrumbHome}</Link>
            <ChevronRight size={12} aria-hidden="true" />
            <Link to="/services">{p.breadcrumbServices}</Link>
            <ChevronRight size={12} aria-hidden="true" />
            <span>{title}</span>
          </nav>
        </div>
      </header>

      {/* ── Carte d'introduction ── */}
      <div className="container stp-intro-wrap">
        <section className="stp-intro">
          <figure className="stp-intro-figure">
            <img src={assetUrl(image)} alt={title} loading="eager" decoding="async" />
          </figure>

          <div className="stp-intro-body">
            <span className="stp-intro-badge">
              <Zap size={13} aria-hidden="true" />
              {p.quoteBadge}
            </span>

            <h2 className="stp-heading">{title}</h2>
            {lead && <p>{lead}</p>}
            {p.introText && <p>{p.introText}</p>}

            {p.introSubTitle && <h3 className="stp-subheading">{p.introSubTitle}</h3>}
            {p.introSubText && <p>{p.introSubText}</p>}

            {displayType.whenHint && (
              <p className="stp-hint">
                <Check size={16} aria-hidden="true" />
                <span>{displayType.whenHint}</span>
              </p>
            )}
          </div>
        </section>
      </div>

      {/* ── Carrousel documents ── */}
      {isInterpretingType ? (
        <DocsCarousel title={carouselTitle} items={interpretingCarouselItems} />
      ) : (
        <DocsCarousel title={carouselTitle} items={carouselItems} seeAllLabel={p.seeAllServices} />
      )}

      {/* ── Corps : contenu + colonne latérale ── */}
      <div className="container stp-layout">
        <main className="stp-main">
          {sections.map((section) => {
            if (!section.paragraphs?.length && section.list?.length) return null;
            return (
              <article key={section.title} className="stp-block">
                <h2 className="stp-heading">{section.title}</h2>
                {section.paragraphs?.map((para) => (
                  <p key={para}>{para}</p>
                ))}
              </article>
            );
          })}

          {subServices.items.length > 0 && (
            <article className="stp-block">
              <h2 className="stp-heading">{p.docsTitle || 'Types de documents'}</h2>
              <ul className="stp-why-list">
                {subServices.items.map((s) => (
                  <li key={s.slug}>
                    <strong>{lang === 'ar' ? (s.titleAr || s.titleFr) : lang === 'en' ? (s.titleEn || s.titleFr) : s.titleFr}</strong>
                  </li>
                ))}
              </ul>
            </article>
          )}

          {p.why?.length > 0 && (
            <article className="stp-block">
              <h2 className="stp-heading">{p.whyTitle}</h2>
              <ul className="stp-why-list">
                {p.why.map((item) => (
                  <li key={item.title}>
                    <strong>{item.title}</strong>
                    <span> — {item.text}</span>
                  </li>
                ))}
              </ul>
              {p.whyClosing && <p>{p.whyClosing}</p>}
              {p.inlineCta && (
                <p className="stp-inline-cta">
                  <Link to="/contact">{p.inlineCta}</Link>
                </p>
              )}
            </article>
          )}

          {displayType.faq?.length > 0 && (
            <article className="stp-block">
              <h2 className="stp-heading">{displayType.faqTitle}</h2>
              <div className="stp-faq-list">
                {displayType.faq.map((item, index) => (
                  <FaqItem
                    key={item.q}
                    item={item}
                    open={openFaq === index}
                    onToggle={() => setOpenFaq((prev) => (prev === index ? -1 : index))}
                  />
                ))}
              </div>
            </article>
          )}


        </main>

        <aside className="stp-aside">
          <div className="stp-side-ctas">
            <Link to={supportsOnlineOrder ? '/order' : '/contact'} className="stp-side-btn">
              <span>{supportsOnlineOrder ? st.wantThisService : p.contactCta}</span>
              <span className="stp-side-btn-arrow" aria-hidden="true">
                <Arrow size={15} />
              </span>
            </Link>
            {!supportsOnlineOrder && (
              <small>{p.quoteCardNote}</small>
            )}
          </div>

          {p.guarantees?.length > 0 && (
            <div className="stp-guarantees">
              <h4>{p.guaranteesTitle}</h4>
              <ul>
                {p.guarantees.map((g) => (
                  <li key={g}>
                    <Check size={15} aria-hidden="true" />
                    <span>{g}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="stp-side-note">
            <p>{p.quoteCardText}</p>
          </div>

          <Link to="/services" className="stp-back">
            {isRTL ? <ArrowRight size={15} aria-hidden="true" /> : <ArrowLeft size={15} aria-hidden="true" />}
            {st.backToChoice}
          </Link>
        </aside>
      </div>

      {/* ── Avis clients ── */}
      {totalReviews > 0 && (
        <section style={{ width: '100%', background: 'linear-gradient(135deg, rgba(22, 101, 52, 0.12) 0%, rgba(161, 98, 7, 0.14) 100%)', padding: '80px 0' }}>
          <div className="container stp-reviews-head">
            <h2 className="stp-heading">Avis clients</h2>
          </div>
          <ReviewsCarousel
            items={[
              { initial: 'S', name: 'Sami B.', role: 'Tunisien résident, France', type: 'Acte de naissance', from: 'Arabe', to: 'Français', text: 'Traduction de mon acte de naissance livrée en 36h. Acceptée sans problème à l\'ambassade de Tunisie à Paris.' },
              { initial: 'I', name: 'Ines M.', role: 'Tunisienne étudiante, Allemagne', type: 'Diplôme universitaire', from: 'Français', to: 'Allemand', text: 'J\'ai fait traduire mon diplôme pour l\'inscription à l\'université en Allemagne. Processus simple, prix clair.' },
              { initial: 'K', name: 'Karim H.', role: 'Tunisien résident, Canada', type: 'Casier judiciaire', from: 'Arabe', to: 'Français', text: 'Excellent support pour mon dossier de visa. Traduction + apostille en une seule démarche.' },
              { initial: 'L', name: 'Laura P.', role: 'Responsable RH, Italie', type: 'Contrat commercial', from: 'Français', to: 'Italien', text: 'Notre entreprise méditerranéenne utilise Tarjemlek pour tous nos documents officiels. Fiable et rapide.' },
            ]}
          />
        </section>
      )}

      {/* ── CTA final ── */}
      <section className="stp-final">
        <div className="container stp-final-inner">
          <div>
            <h2>{p.finalTitle}</h2>
            <p>{p.finalText}</p>
          </div>
          <div className="stp-final-actions">
            <Link to={supportsOnlineOrder ? '/order' : '/contact'} className="stp-btn stp-btn-gold">
              {supportsOnlineOrder ? st.wantThisService : p.contactCta}
              <Arrow size={17} aria-hidden="true" />
            </Link>
            {supportsOnlineOrder && (
              <Link to="/contact" className="stp-btn stp-btn-ghost">
                {p.contactCta}
              </Link>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
